// Human-readable summaries of a skip-logic section's rules, for the builder's
// review screen. Pure: given the survey items (to label destinations and
// condition subjects) and a section, it returns strings the UI renders - e.g.
// "If Role is any of Teacher, go to Subject" and "Otherwise, go to the end of
// the survey". Operator wording here is for display only; it does not decide
// which operators are valid (that lives with the engine and the condition
// editor).
//
// Conditions are in the rules-JSON shape: an {all|any} group of leaves, each
// leaf an { answer: { item, op, values | number | min/max } }, a
// { participant: { op, values } } or an { attribute: { key, op, values } }, or a
// server-projected { fact }. Choice values are option values; turning them into
// option labels is the condition editor's job, so here a bare value is shown.

import {ITEM_TYPE} from '../constants'
import {ATTRIBUTE_KEYS, PARTICIPANT_TYPES, optionsForItem} from './conditions'

const itemById = (survey, itemId) =>
  (survey?.items || []).find(item => item.itemId === itemId)

const itemLabel = item => {
  if (!item) return 'a later item'
  if (item.itemType === ITEM_TYPE.CONTENT) {
    return item.content?.headline?.trim() || 'a content screen'
  }
  if (item.itemType === ITEM_TYPE.SKIP_LOGIC) return 'a skip-logic section'
  return (
    (item.staffText || item.parentText || item.pupilText || item.text || '').trim() ||
    'a question'
  )
}

// Where a destination sends the respondent, in words.
export const describeGoTo = (survey, goTo) => {
  if (!goTo) return 'the next question'
  if (goTo.end === true) return 'the end of the survey'
  if (goTo.item != null) return itemLabel(itemById(survey, goTo.item))
  return 'the next question'
}

const OPERATOR_PHRASES = {
  is: 'is',
  isNot: 'is not',
  isAnyOf: 'is any of',
  isNoneOf: 'is none of',
  includesAnyOf: 'includes any of',
  includesAllOf: 'includes all of',
  includesNoneOf: 'includes none of',
  includesExactly: 'is exactly',
  eq: 'equals',
  ne: 'does not equal',
  gt: 'is more than',
  gte: 'is at least',
  lt: 'is less than',
  lte: 'is at most',
  between: 'is between',
  notAnswered: 'was not answered',
}

// Choice values are option values; resolve them to the question's option labels
// for the summary (a value with no matching option is shown as itself).
const answerValueText = (survey, answer) => {
  if (answer.op === 'notAnswered') return ''
  if (answer.op === 'between') return `${answer.min} and ${answer.max}`
  if (answer.number != null) return String(answer.number)
  if (Array.isArray(answer.values)) {
    const options = optionsForItem(itemById(survey, answer.item))
    return answer.values
      .map(v => options.find(o => o.value === v)?.label ?? String(v))
      .join(', ')
  }
  return ''
}

const describeAnswer = (survey, answer) => {
  const subject = itemLabel(itemById(survey, answer.item))
  const phrase = OPERATOR_PHRASES[answer.op] || answer.op || 'matches'
  const value = answerValueText(survey, answer)
  return value ? `${subject} ${phrase} ${value}` : `${subject} ${phrase}`
}

const setPhrase = op => (op === 'isNoneOf' ? 'is none of' : 'is any of')

const describeLeaf = (survey, cond) => {
  if (cond?.answer) return describeAnswer(survey, cond.answer)
  if (cond?.participant) {
    const labels = (cond.participant.values || []).map(
      v => PARTICIPANT_TYPES.find(p => p.value === v)?.label ?? v
    )
    return `the participant type ${setPhrase(cond.participant.op)} ${labels.join(', ')}`
  }
  if (cond?.attribute) {
    const spec = ATTRIBUTE_KEYS.find(k => k.key === cond.attribute.key)
    const name = spec?.label ?? cond.attribute.key
    return `${name} ${setPhrase(cond.attribute.op)} ${(cond.attribute.values || []).join(', ')}`
  }
  if (cond?.fact != null) return 'a participant condition'
  return 'a condition'
}

const describeGroup = (survey, group) => {
  const mode = Array.isArray(group?.any) ? 'any' : 'all'
  const join = mode === 'any' ? ' or ' : ' and '
  return (group?.[mode] || [])
    .map(c =>
      c?.all || c?.any
        ? `(${describeGroup(survey, c)})`
        : describeLeaf(survey, c)
    )
    .join(join)
}

// A single rule as "If <conditions>, go to <destination>", or "Always go to
// <destination>" when it has no conditions yet.
export const describeRule = (survey, rule) => {
  const dest = describeGoTo(survey, rule?.goTo)
  const when = describeGroup(survey, rule?.when)
  return when ? `If ${when}, go to ${dest}` : `Always go to ${dest}`
}

// Structured summary of a section for the review screen.
export const summarizeSection = (survey, section) => ({
  rules: (section?.rules?.rules || []).map((rule, i) => ({
    number: i + 1,
    text: describeRule(survey, rule),
  })),
  otherwise: `Otherwise, go to ${describeGoTo(survey, section?.rules?.otherwise)}`,
})
